// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Badge, Tooltip, AppIcon } from '@loa/ui-components';
import type { ClientProfileStatus, RelationshipDerivedStatus } from '../types/client';

type BadgeColor = 'grey' | 'orange' | 'yellow' | 'green' | 'red' | 'blue' | 'teal' | 'neutral' | 'purple';

type StatusBadgeProps = Readonly<{
  status: ClientProfileStatus | RelationshipDerivedStatus | string;
  type: 'client' | 'relationship';
  /** Reserved — currently the badge always renders at the shared "sm" size.
   *  Kept on the prop type so callers (and tests) that pass `size` don't
   *  have to be updated. */
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large';
  showIcon?: boolean;
  showTooltip?: boolean;
}>;

interface StatusConfig {
  color: BadgeColor;
  icon: string;
  text: string;
  description: string;
}

// Client profile status configurations (WCAG AA compliant)
const clientStatusConfig: Record<string, StatusConfig> = {
  DRAFT: {
    color: 'grey',
    icon: 'clock outline',
    text: 'Draft',
    description: 'Client profile created, not yet invited'
  },
  INVITED: {
    color: 'yellow',
    icon: 'mail',
    text: 'Invited',
    description: 'Invitation sent, awaiting registration'
  },
  PENDING_SIGNATURE: {
    color: 'orange',
    icon: 'file alternate outline',
    text: 'Pending Signature',
    description: 'LOA pending client signature'
  },
  SIGNED: {
    color: 'green',
    icon: 'check circle',
    text: 'Signed',
    description: 'Client has signed at least one LOA'
  },
  INACTIVE: {
    color: 'orange',
    icon: 'pause circle',
    text: 'Inactive',
    description: 'Client temporarily disabled'
  },
  ARCHIVED: {
    color: 'neutral',
    icon: 'archive',
    text: 'Archived',
    description: 'Client profile archived'
  },
  // Legacy status mappings
  prospect: {
    color: 'grey',
    icon: 'clock outline',
    text: 'Prospect',
    description: 'Not yet invited'
  },
  invited: {
    color: 'yellow',
    icon: 'mail',
    text: 'Invited',
    description: 'Invitation sent'
  },
  active: {
    color: 'blue',
    icon: 'check circle',
    text: 'Active',
    description: 'Authenticated on platform'
  },
  inactive: {
    color: 'orange',
    icon: 'pause circle',
    text: 'Inactive',
    description: 'Temporarily disabled'
  }
};

// Relationship derived status configurations (WCAG AA compliant)
const relationshipStatusConfig: Record<string, StatusConfig> = {
  INCOMPLETE: {
    color: 'orange',
    icon: 'warning sign',
    text: 'Incomplete',
    description: 'Missing required components'
  },
  NEEDS_ATTENTION: {
    color: 'red',
    icon: 'attention',
    text: 'Needs Attention',
    description: 'Expired invitations or stuck LOAs'
  },
  ACTIVE: {
    color: 'blue',
    icon: 'check circle',
    text: 'Active',
    description: 'All components in good standing'
  },
  PARTIALLY_SIGNED: {
    color: 'yellow',
    icon: 'clipboard list',
    text: 'Partially Signed',
    description: 'Some clients signed, others pending'
  },
  FULLY_SIGNED: {
    color: 'green',
    icon: 'check circle outline',
    text: 'Fully Signed',
    description: 'All active clients have signed LOAs'
  },
  ARCHIVED: {
    color: 'neutral',
    icon: 'archive',
    text: 'Archived',
    description: 'Relationship archived'
  },
  // Legacy status mappings
  active: {
    color: 'green',
    icon: 'check circle',
    text: 'Active',
    description: 'Relationship is active'
  },
  inactive: {
    color: 'yellow',
    icon: 'pause circle',
    text: 'Inactive',
    description: 'Relationship is inactive'
  },
  archived: {
    color: 'grey',
    icon: 'archive',
    text: 'Archived',
    description: 'Relationship is archived'
  }
};

// Default config for unknown statuses
const defaultConfig: StatusConfig = {
  color: 'grey',
  icon: 'question circle',
  text: 'Unknown',
  description: 'Unknown status'
};

export function StatusBadge({
  status,
  type,
  showIcon = true,
  showTooltip = true
}: StatusBadgeProps) {
  // Handle undefined/null status
  if (!status) {
    return (
      <Badge color="grey" size="sm" aria-label="Status: Unknown">
        {showIcon && <AppIcon name="question circle" />}
        Unknown
      </Badge>
    );
  }

  const configMap = type === 'client' ? clientStatusConfig : relationshipStatusConfig;
  const config = configMap[status] || {
    ...defaultConfig,
    text: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replaceAll('_', ' ')
  };

  const badge = (
    <Badge
      color={config.color}
      size="sm"
      aria-label={`Status: ${config.text}`}
    >
      {showIcon && <AppIcon name={config.icon} />}
      {config.text}
    </Badge>
  );

  if (showTooltip && config.description) {
    return (
      <Tooltip
        trigger={badge}
        content={config.description}
        size="mini"
        position="top"
      />
    );
  }

  return badge;
}

export default StatusBadge;
